<script>
import { required, email } from "vuelidate/lib/validators";

import {
  authMethods,
  authFackMethods,
  notificationMethods,
} from "@/state/helpers";

import api from "@/helpers/api-rest/api.js";
import { errorMessages } from "@/helpers/validations/errorMessages";
import InstitutionalLogosBanner from "@/components/institutional-logos-banner.vue";
import AppBrandLogo from "@/components/AppBrandLogo.vue";
import {
  loadSiteIdentity,
  applyLoginBannerCssVariable,
} from "@/helpers/site-identity/site-identity-api.js";

export default {
  components: {
    InstitutionalLogosBanner,
    AppBrandLogo,
  },
  data() {
    return {
      email: "",
      password: "",
      submitted: false,
      loading: false,
      errorMessages,
      showPassword: false,
    };
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  created() {
    document.body.classList.add("auth-body-bg");
  },
  async mounted() {
    const identity = await loadSiteIdentity();
    applyLoginBannerCssVariable(identity?.login_banner);
  },
  validations: {
    email: { required, email },
    password: { required },
  },
  methods: {
    ...authMethods,
    ...authFackMethods,
    ...notificationMethods,
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      this.submitted = true;

      this.$store.dispatch("notification/clear");

      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        this.loading = true;
        const apiEndpoint = `/api/v1/auth/login`;
        api
          .post({
            url: apiEndpoint,
            data: {
              email: this.email,
              password: this.password,
            },
          })
          .then((response) => {
            this.loading = false;
            const token = response.data.data.token;
            const user = response.data.data.user;

            this.setUser(user);
            localStorage.setItem("authToken", token);
            this.$router.push({ name: "home" });
          })
          .catch((error) => {
            this.loading = false;
            const errorMessage =
              error?.response?.data?.errors[0]?.title || "Error inesperado";
            this.$store.dispatch("notification/error", errorMessage);
          });
      }
    },
  },
};
</script>

<template>
  <div class="auth-page-wrapper">
    <!-- Background decorative elements -->
    <div class="auth-background">
      <div class="auth-shape auth-shape-1"></div>
      <div class="auth-shape auth-shape-2"></div>
      <div class="auth-shape auth-shape-3"></div>
    </div>

    <!-- Main content -->
    <div class="auth-container">
      <div class="auth-card">
        <!-- Logo Section -->
        <div class="auth-logo-section">
          <div class="auth-logo-wrapper">
            <AppBrandLogo class-name="auth-main-logo" />
          </div>
          <h1 class="auth-welcome-title">¡Bienvenido de nuevo!</h1>
          <p class="auth-welcome-subtitle">
            Inicia sesión para continuar con tu cuenta
          </p>
        </div>

        <!-- Alert Messages -->
        <b-alert
          :variant="notification.type"
          class="auth-alert"
          v-if="notification.message"
          show
          dismissible
        >
          {{ notification.message }}
        </b-alert>

        <!-- Login Form -->
        <form class="auth-form" @submit.prevent="tryToLogIn">
          <!-- Email Field -->
          <div class="auth-form-group">
            <label for="email" class="auth-label">
              <i class="ri-mail-line auth-label-icon"></i>
              Correo electrónico
            </label>
            <div class="auth-input-wrapper">
              <input
                type="email"
                v-model="email"
                class="auth-input"
                id="email"
                placeholder="tu@email.com"
                :class="{
                  'auth-input-error': submitted && $v.email.$error,
                }"
              />
              <div
                v-if="submitted && $v.email.$error"
                class="auth-error-message"
              >
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.email.required">{{
                  errorMessages.required
                }}</span>
                <span v-if="!$v.email.email"
                  >Por favor, introduce un email válido.</span
                >
              </div>
            </div>
          </div>

          <!-- Password Field -->
          <div class="auth-form-group">
            <label for="userpassword" class="auth-label">
              <i class="ri-lock-2-line auth-label-icon"></i>
              Contraseña
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="auth-input"
                id="userpassword"
                placeholder="••••••••"
                :class="{ 'auth-input-error': $v.password.$error }"
              />
              <button
                type="button"
                class="auth-password-toggle"
                @click="togglePasswordVisibility"
              >
                <i
                  :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                ></i>
              </button>
              <div v-if="$v.password.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.password.required">{{
                  errorMessages.required
                }}</span>
              </div>
            </div>
          </div>

          <!-- Forgot Password Link -->
          <div class="auth-form-options">
            <router-link
              to="/forgot-password"
              class="auth-link auth-link-forgot"
            >
              <i class="ri-lock-password-line"></i>
              ¿Olvidaste tu contraseña?
            </router-link>
          </div>

          <!-- Submit Button -->
          <button
            class="auth-submit-btn"
            type="submit"
            :disabled="loading"
          >
            <span v-if="loading" class="auth-spinner"></span>
            <span v-else>
              <i class="ri-login-box-line"></i>
              Iniciar sesión
            </span>
          </button>
        </form>

        <!-- Register Link -->
        <div class="auth-footer">
          <p class="auth-footer-text">
            ¿No tienes una cuenta?
            <router-link to="/register" class="auth-link auth-link-register">
              Regístrate aquí
            </router-link>
          </p>
        </div>
      </div>

      <!-- Decorative side panel (hidden on mobile) -->
      <div class="auth-side-panel">
        <div class="auth-side-content">
          <h2 class="auth-side-title">CEX</h2>
          <p class="auth-side-description">
            Plataforma de gestión integral para tu organización
          </p>
          <div class="auth-side-features">
            <div class="auth-feature-item">
              <i class="ri-shield-star-line"></i>
              <span>Seguro y confiable</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-speed-line"></i>
              <span>Rápido y eficiente</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-customer-service-2-line"></i>
              <span>Soporte técnico</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Institutional Logos Banner -->
    <div class="auth-logos-banner-wrapper">
      <InstitutionalLogosBanner variant="default" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
// All styles are now in _authentication.scss for better maintainability
</style>
